import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import Slider from '../Components/SliderNews';

const API_BASE_URL = 'https://back.zubilaw.com';

const toAbsoluteMediaUrl = (mediaPath) => {
  if (!mediaPath) {
    return null;
  }

  return mediaPath.startsWith('http') ? mediaPath : `${API_BASE_URL}/${mediaPath}`;
};

const buildInitialNewsItem = (locationState, newsId) => {
  if (!locationState) {
    return null;
  }

  return {
    id: newsId,
    title: locationState.title || '',
    paragraph: locationState.paragraph || locationState.description || '',
    image: locationState.image || locationState.imageSrc || '',
    detailMedia: Array.isArray(locationState.detailMedia) ? locationState.detailMedia : [],
    detailVideo: locationState.detailVideo || null,
  };
};

const getDetailImages = (newsItem) => {
  if (!newsItem) {
    return [];
  }

  const uploadedDetailImages = Array.isArray(newsItem.detailMedia)
    ? newsItem.detailMedia.filter(Boolean)
    : [];

  if (uploadedDetailImages.length > 0) {
    return uploadedDetailImages;
  }

  const fallbackImage = newsItem.image || newsItem.imageSrc;
  return fallbackImage ? [fallbackImage] : [];
};

const NewsDetail = () => {
  const location = useLocation();
  const { id: newsId } = useParams();
  const [newsItem, setNewsItem] = useState(() => buildInitialNewsItem(location.state, newsId));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setNewsItem((currentNewsItem) => {
      const initialNewsItem = buildInitialNewsItem(location.state, newsId);

      if (!initialNewsItem) {
        return currentNewsItem;
      }

      return {
        ...(currentNewsItem || {}),
        ...initialNewsItem,
      };
    });
  }, [location.state, newsId]);

  useEffect(() => {
    let isMounted = true;

    const fetchNewsItem = async () => {
      if (!newsId) {
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/featureSection/${newsId}`);
        if (isMounted && response.data && response.data.success) {
          setNewsItem(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching news item:', error);
      }
    };

    fetchNewsItem();

    return () => {
      isMounted = false;
    };
  }, [newsId]);

  const title = newsItem?.title || '';
  const description = newsItem?.paragraph || newsItem?.description || '';
  const detailImages = getDetailImages(newsItem);
  const detailVideoUrl = toAbsoluteMediaUrl(newsItem?.detailVideo);
  const hasMedia = Boolean(detailVideoUrl) || detailImages.length > 0;

  return (
    <>
      <div className="container bg-[#777777] items-center mx-auto xl:max-w-7xl w-full px-0 md:px-0 xl:px-0 pb-24" style={{ backgroundColor: 'white' }}>
        <Slider News="NEWS" />
        <div className="mx-auto px-4 sm:px-6 lg:px-10 py-10 mt-0 flex flex-col gap-10 lg:gap-20">
          <div className="flex flex-col items-start justify-start mt-0 lg:mt-14 text-[#972a36] ml-4">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold mb-0 text-start" style={{ fontFamily: 'Arial' }}>
              {title}
            </h1>
            <span className="bg-[#777777] h-0.5 w-16 mt-2 mb-14" />
            <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-20 mb-14">
              <div className="flex-grow">
                <div
                  className="text-black text-start m-0"
                  style={{ textAlign: 'justify', textJustify: 'inter-word', fontFamily: 'Arial' }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </div>
              {hasMedia && (
                <div className="flex-none w-full lg:w-96 mt-4 lg:mt-0 space-y-4">
                  {detailImages.length === 1 && (
                    <img
                      src={toAbsoluteMediaUrl(detailImages[0])}
                      alt={title}
                      className="w-full object-contain rounded-xl"
                    />
                  )}
                  {detailImages.length > 1 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                      {detailImages.map((imagePath, index) => (
                        <img
                          key={`${imagePath}-${index}`}
                          src={toAbsoluteMediaUrl(imagePath)}
                          alt={`${title} ${index + 1}`}
                          className="w-full object-contain rounded-xl"
                        />
                      ))}
                    </div>
                  )}
                  {detailVideoUrl && (
                    <video
                      controls
                      className="w-full rounded-xl bg-black"
                      style={{ maxHeight: '420px' }}
                    >
                      <source src={detailVideoUrl} />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="w-full border-t border-gray-300 mb-5 mx-auto" style={{ maxWidth: '50%' }}></div>
      </div>
    </>
  );
};

export default NewsDetail;
